'use client';

import React from 'react';

/**
 * The band directly beneath the hero.
 *
 * The homepage had a real gap here: nine sections made claims and none of them
 * offered a visitor any reason to believe the claims or any sense of what
 * engaging would cost them. The obvious fix is a logo wall or a row of
 * statistics — but there are no published client stories yet (which is why
 * TestimonialsSection correctly renders nothing), and inventing "24 clients ·
 * 4.9/5" to fill the space is exactly the kind of thing a consulting buyer
 * checks and never forgives.
 *
 * So this states the commercial terms instead. Every line is something the
 * site already commits to further down; bringing them to the top answers the
 * two questions that actually stop an SMB owner from booking — "what is this
 * going to cost me" and "what am I agreeing to" — with facts rather than
 * flattery. It is weaker proof than a named client and stronger proof than a
 * fabricated number.
 *
 * When real stories exist, this is the right place to put a logo row: keep the
 * band, swap the four terms for the marks, move the terms into the closing
 * block beside the booking button.
 */
const TERMS = [
  { value: 'Free', label: 'Initial consultation' },
  { value: 'None', label: 'Long-term contracts' },
  { value: 'Agreed', label: 'Scope, before you commit' },
  { value: 'EU · AU', label: 'Where we work' },
];

export default function TermsStripSection() {
  return (
    <section
      data-ground
      id="terms"
      aria-label="How we work"
      style={{
        /* This one keeps its rules: it is a measured strip, and the hairlines
           are part of what it is saying. So it takes the wash without the fade
           — the borders are the edge here. */
        background: 'var(--m-panel)',
        borderTop: '1px solid var(--m-rule)',
        borderBottom: '1px solid var(--m-rule)',
      }}
    >
      <div className="m-container">
        {/* The bleed is gone on phones. `-mx-5` pulled the grid back out
            through the container's own 20px of gutter, so the left column began
            12px from the panel edge — the cell's padding and nothing else — and
            read as shoved against it. Dropping it lets the container's gutter
            count, which puts the first value 32px in and lines it up with every
            other section on the page. Above `lg` the four-up row still runs
            edge to edge, which is where the bleed was earning its keep.

            Vertical padding 32 → 26, a fifth off, with the cells following
            9 → 7, and 26 → 16 below `lg`.

            The phone needs less of it than the desktop does, not the same:
            above `lg` this is one four-up row and the padding is the only
            vertical space the strip has, while below it the four terms stack
            into two rows and each cell adds 7px of its own. Same 26px at both
            sizes and the stacked version carries half again as much air as the
            flat one — 171px of band for four short lines of type, against 91px
            for the same four. The hairlines are what mark the strip's edges
            here; the padding only has to keep the values off them. */}
        <div className="grid grid-cols-2 lg:grid-cols-4 lg:-mx-5 py-4 lg:py-[26px]">
          {TERMS.map((t) => (
            <div
              key={t.label}
              /* Top-aligned, not centred. Centring made each value sit at the
                 vertical middle of its own cell, so on a phone — where the
                 labels wrap to different line counts — the four values landed
                 on four different baselines.

                 The divider is suppressed on the first cell of each row, which
                 is a different cell at each breakpoint: every other one in the
                 two-column phone grid, every fourth in the four-column desktop
                 one.

                 Padding is deliberately asymmetric: a cell that follows a
                 divider gets more of it on the left. 9px against a hairline
                 reads as text crowding the rule, while the same 9px against
                 nothing reads as a normal gutter — so the cells with a border
                 take 20px on a phone and 26px on desktop, and the ones that
                 start a row keep the tighter figure. Padding every cell equally
                 would have had to spend that space on the outer edge too, where
                 it was not wanted and where the row has none to give. */
              className="flex flex-col justify-start px-[9px] py-[7px] border-l odd:border-l-0 even:pl-[16px] lg:py-0 lg:px-[19px] lg:even:pl-[19px] lg:odd:border-l lg:[&:nth-child(4n+1)]:border-l-0 lg:[&:not(:nth-child(4n+1))]:pl-[26px]"
              style={{ borderColor: 'var(--m-rule)' }}
            >
              <span
                className="m-tnum"
                style={{
                  fontFamily: "'Archivo Narrow', 'Roboto Condensed', sans-serif",
                  /* 28 → 16, and the box scaled with it rather than left
                     behind: padding 20/16 → 12/9 and the gap under the value
                     6 → 4, all the same 0.64 the type took. Padding that stays
                     put while type shrinks is what leaves a strip looking
                     hollow, so the cell tightens by the same proportion.

                     The label under each value stays at 11px. It is already at
                     the floor the mobile audit set, and shrinking a monospace
                     label that people actually have to read is how that audit's
                     work gets quietly undone. At 16 against 11 the hierarchy now
                     rests on weight and family — bold condensed against letter-
                     spaced mono — rather than on size alone. */
                  fontSize: 16,
                  fontWeight: 700,
                  lineHeight: 1.1,
                  letterSpacing: '0.02em',
                  textTransform: 'uppercase',
                  color: 'var(--m-ink)',
                }}
              >
                {t.value}
              </span>
              {/* Tighter tracking than the shared eyebrow, and only here.
                  Giving the strip its proper left gutter cost the grid 40px,
                  which pushed three of the four labels onto a second line;
                  0.16em of letter-spacing was most of why they were long
                  enough to. 0.1em buys the width back at the same 11px, so the
                  labels stay legible and fit on one line. Overridden inline
                  rather than in .m-eyebrow, which a dozen other places use at
                  widths where the wider tracking is right. */}
              <span className="m-eyebrow" style={{ marginTop: 4, letterSpacing: '0.1em' }}>
                {t.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
